import {
  Sorting,
  ColumnWidthInfo,
  ColumnExtension,
  GridFilter,
} from "../../common/store/types";
import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { EditionDocumentation, SortOrder } from "../../../api/graphql";
import type { DataGridColumn } from "../../../common/controls/lists/DataGrid";
import {
  isOperatorAvailable,
  type FilterOperator,
} from "../../../common/services/mrtFilterService";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import { editionContractPublisherDisplayId } from "../../editionContractPublisher/store/EditionContractPublisherCommonStore";

export type ColumnInfo = Record<
  string,
  {
    sortingEnabled: boolean;
    width: number;
    isHidden: boolean;
  }
>;

const VERSION = "1.0.0";

export type RowClickViewMode = "route" | "drawer";

export type EditionDocumentationListProfile = {
  VERSION: string;
  columnOrder: string[];
  columnsInfo: ColumnInfo;
  sorting: Sorting[];
  filtering: GridFilter[];
  showColumnFilters?: boolean;
  viewMode?: RowClickViewMode;
};

export class EditionDocumentationGridDataStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  errorDescription: string = "";
  errorDialogOpened: boolean = false;
  filtering: GridFilter[] = [];

  // Estado de UI del DataGrid (sincronizado con ListToolbox)
  showColumnFilters: boolean = false;
  isFullScreen: boolean = false;
  columnMenuOpen: boolean = false;
  viewMode: RowClickViewMode = "route";

  get ProfileKey(): string[] {
    return ["EditionDocumentation", "list"];
  }

  columnsInfo: ColumnInfo = {};
  initialColumnsInfo: ColumnInfo = {};

  sortExpression: Record<
    string,
    (direction: SortOrder, lang: LangStore) => any
  > = {
    works: (direction: SortOrder, _lang: LangStore) => {
      return { works: { _count: direction } };
    },
    territories: (direction: SortOrder, _lang: LangStore) => {
      return { territories: { _count: direction } };
    },
    authors: (direction: SortOrder, _lang: LangStore) => {
      return { authors: { _count: direction } };
    },
    publishers: (direction: SortOrder, _lang: LangStore) => {
      return { publishers: { _count: direction } };
    },
  };

  initialColumnOrder = [
    "finalDocumentPdf",
    "documentName",
    "works",
    "territories",
    "authors",
    "authorsDisplay",
    "publishers",
    "publisherConfiguration",
    "publisherConfigurationDisplay",
    "territoriesDisplay",
    "contractDuration",
    "contractRenewal",
    "contractNumber",
    "id",
  ];
  columnOrder = [
    "finalDocumentPdf",
    "documentName",
    "works",
    "territories",
    "authors",
    "authorsDisplay",
    "publishers",
    "publisherConfiguration",
    "publisherConfigurationDisplay",
    "territoriesDisplay",
    "contractDuration",
    "contractRenewal",
    "contractNumber",
    "id",
  ];

  _sorting: Sorting[];

  // Restricciones de rol por columna (columnas que requieren roles específicos)
  readonly columnRoleRestrictions: Record<string, string[]> = {};

  // Columna de grilla → campo real por el que filtrar. Las pseudo-columnas del
  // snapshot (`data_.X`) no existen en SQL: filtran por la columna promovida
  // con .searchable.
  readonly columnFilterAlias: Record<string, string> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.columnsInfo = {
      finalDocumentPdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      documentName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      works: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      territories: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authors: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorsDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publishers: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publisherConfiguration: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publisherConfigurationDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      territoriesDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractDuration: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractRenewal: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this.initialColumnsInfo = {
      finalDocumentPdf: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      documentName: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      works: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      territories: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authors: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorsDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publishers: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publisherConfiguration: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      publisherConfigurationDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      territoriesDisplay: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractDuration: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractRenewal: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      contractNumber: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this._sorting = [
      { columnName: "contractNumber", direction: SortOrder.Asc },
    ];
    makeAutoObservable(this);
  }

  // Verifica si el usuario actual tiene permiso para ver una columna
  isColumnAllowedForUser(columnName: string): boolean {
    const requiredRoles = this.columnRoleRestrictions[columnName];
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No hay restricción
    }
    return requiredRoles.some((role) => this.rootStore.authStore.hasRole(role));
  }

  get subFilters(): Record<
    string,
    { subFields: string[]; multi: boolean } | undefined
  > {
    return {
      works: {
        subFields: ["title"],
        multi: true,
      },
      territories: {
        subFields: [this.langStore.fieldByLang("name")],
        multi: true,
      },
      authors: {
        subFields: ["firstName", "lastName"],
        multi: true,
      },
      publishers: {
        subFields: ["publisher.pseudonym"],
        multi: true,
      },
    };
  }

  get searchFields(): string[] {
    return [];
  }

  get sorting() {
    return this._sorting;
  }

  set sorting(sorting: Sorting[]) {
    this._sorting = sorting;
  }

  get mrtColumnVisibility(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = !info.isHidden;
    }
    return result;
  }

  setMrtColumnVisibility(visibility: Record<string, boolean>) {
    for (const [key, isVisible] of Object.entries(visibility)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].isHidden = !isVisible;
        // Si se oculta la columna, borrar su filtro (UX: lo que ves es lo que filtras)
        if (!isVisible) {
          this.filtering = this.filtering.filter((f) => f.columnName !== key);
        }
      }
    }
  }

  showAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = false;
    }
  }

  hideAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = true;
    }
  }

  // Métodos para estado de UI del DataGrid
  setShowColumnFilters(value: boolean) {
    this.showColumnFilters = value;
  }

  toggleShowColumnFilters() {
    this.showColumnFilters = !this.showColumnFilters;
  }

  setIsFullScreen(value: boolean) {
    this.isFullScreen = value;
  }

  toggleIsFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  setColumnMenuOpen(value: boolean) {
    this.columnMenuOpen = value;
  }

  openColumnMenu() {
    this.columnMenuOpen = true;
  }

  closeColumnMenu() {
    this.columnMenuOpen = false;
  }

  setViewMode(mode: RowClickViewMode) {
    this.viewMode = mode;
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === "drawer" ? "route" : "drawer";
  }

  get mrtColumnSizing(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = info.width;
    }
    return result;
  }

  setMrtColumnSizing(sizing: Record<string, number>) {
    for (const [key, width] of Object.entries(sizing)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].width = width;
      }
    }
  }

  get columnWidths(): ColumnWidthInfo[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return { columnName: key, width: this.columnsInfo[key].width };
    });
  }

  setColumnWidths(columnWidths: ColumnWidthInfo[]) {
    for (const cw of columnWidths) {
      this.columnsInfo[cw.columnName].width = Number(cw.width);
    }
  }

  get columnExtensions(): ColumnExtension[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return {
        columnName: key,
        sortingEnabled: this.columnsInfo[key].sortingEnabled,
      };
    });
  }

  setColumnExtensions(columnExtensions: ColumnExtension[]) {
    for (const ce of columnExtensions) {
      this.columnsInfo[ce.columnName].sortingEnabled = ce.sortingEnabled;
    }
  }

  setColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  resetColumnOrder() {
    this.columnOrder = [...this.initialColumnOrder];
    // Restaurar visibilidad y ancho inicial de columnas
    for (const key of Object.keys(this.columnsInfo)) {
      if (this.initialColumnsInfo[key]) {
        this.columnsInfo[key].isHidden = this.initialColumnsInfo[key].isHidden;
        this.columnsInfo[key].width = this.initialColumnsInfo[key].width;
      }
    }
  }

  setSorting(sorting: Sorting[]) {
    this.sorting = sorting;
  }

  get columnTypes() {
    return {
      finalDocumentPdf: "File",
      documentName: "String",
      works: "__Reference",
      territories: "__Reference",
      authors: "__Reference",
      authorsDisplay: "String",
      publishers: "__SubEntity",
      publisherConfiguration: "String",
      publisherConfigurationDisplay: "String",
      territoriesDisplay: "String",
      contractDuration: "Int",
      contractRenewal: "Int",
      contractNumber: "String",
      id: "String",
    } as Record<string, string>;
  }

  get filteringColumns() {
    return [
      { columnName: "finalDocumentPdf", filteringEnabled: true },
      { columnName: "documentName", filteringEnabled: true },
      { columnName: "works", filteringEnabled: true },
      { columnName: "territories", filteringEnabled: true },
      { columnName: "authors", filteringEnabled: true },
      { columnName: "authorsDisplay", filteringEnabled: true },
      { columnName: "publishers", filteringEnabled: true },
      { columnName: "publisherConfiguration", filteringEnabled: true },
      { columnName: "publisherConfigurationDisplay", filteringEnabled: true },
      { columnName: "territoriesDisplay", filteringEnabled: true },
      { columnName: "contractDuration", filteringEnabled: true },
      { columnName: "contractRenewal", filteringEnabled: true },
      { columnName: "contractNumber", filteringEnabled: true },
      { columnName: "id", filteringEnabled: true },
    ];
  }

  serverColumn(columnName: string) {
    return columnName;
  }

  setSelectedFilters(filters: GridFilter[]) {
    this.filtering = filters;
  }

  get serverFilters() {
    // Solo aplicar filtros si están visibles (showColumnFilters = true)
    return this.showColumnFilters ? this.filtering : [];
  }

  serverSearch(value: string) {
    return this.searchFields.map((x) => {
      return {
        columnName: x,
        operation: "contains",
        value: value,
      };
    });
  }

  get defaultFilters() {
    return this.filtering;
  }

  get numberColumns() {
    return ["contractDuration", "contractRenewal"];
  }

  get booleanColumns() {
    return [];
  }

  get dateColumns() {
    return [];
  }

  get nullableDateColumns() {
    return [] as string[];
  }

  get xlsColumns(): { header: string; key: string; width: number }[] {
    const columns = [];
    for (const column of this.columnOrder) {
      if (
        !this.columnsInfo[column].isHidden &&
        (column.length < 2 || column.slice(0, 2) !== "__")
      ) {
        const width = (this.columnsInfo[column].width / 10) * 1.33;
        columns.push({
          header: this.columnTitles[column],
          key: column,
          width,
        });
      }
    }
    return columns;
  }

  get OrderBy() {
    let orderBy = [];
    for (const s of this.sorting) {
      const sortExpression = this.sortExpression[s.columnName];
      if (sortExpression) {
        const ob = sortExpression(
          s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
          this.langStore
        );
        orderBy.push(ob);
      } else {
        orderBy.push({
          [s.columnName]:
            s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
        });
      }
    }
    return orderBy;
  }

  get columnTitles(): Record<string, string> {
    return {
      works: `${this.langStore.EditionDocumentationWorksLabel}`,
      territories: `${this.langStore.EditionDocumentationTerritoriesLabel}`,
      authors: `${this.langStore.EditionDocumentationAuthorsLabel}`,
      authorsDisplay: `${this.langStore.EditionDocumentationAuthorsDisplayLabel}`,
      publishers: `${this.langStore.EditionDocumentationPublishersLabel}`,
      publisherConfiguration: `${this.langStore.EditionDocumentationPublisherConfigurationLabel}`,
      publisherConfigurationDisplay: `${this.langStore.EditionDocumentationPublisherConfigurationDisplayLabel}`,
      territoriesDisplay: `${this.langStore.EditionDocumentationTerritoriesDisplayLabel}`,
      contractDuration: `${this.langStore.EditionDocumentationContractDurationLabel}`,
      contractRenewal: `${this.langStore.EditionDocumentationContractRenewalLabel}`,
      contractNumber: `${this.langStore.EditionDocumentationContractNumberLabel}`,
      documentName: `${this.langStore.EditionDocumentationDocumentNameLabel}`,
      finalDocumentPdf: `${this.langStore.EditionDocumentationFinalDocumentPdfLabel}`,
      id: `Id`,
    };
  }

  // Un date (día de calendario) filtra en su propia columna, sin zona ni hora
  // ('calendarDate'); un dateTime sigue siendo 'date'.
  private getMrtColumnType(
    type: string
  ): "string" | "number" | "date" | "calendarDate" | "boolean" {
    if (type === "Int" || type === "Decimal") return "number";
    if (type === "Date") return "calendarDate";
    if (type === "DateTime") return "date";
    if (type === "Boolean") return "boolean";
    return "string";
  }

  get dataGridColumns(): DataGridColumn<Record<string, any>>[] {
    // Usar initialColumnOrder (orden fijo) para definir columnas
    // MRT maneja reorden visual con prop columnOrder separada
    // Esto permite que "Reset Order" funcione correctamente
    const nullableDateColumns = this.nullableDateColumns;
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => {
        const columnType = this.columnTypes[name];
        const filterCol = this.filteringColumns.find(
          (f: any) => f.columnName === name
        );
        const baseColumn = {
          header: this.columnTitles[name] || name,
          size: this.columnsInfo[name]?.width || 150,
          columnType: this.getMrtColumnType(columnType),
          nullable: nullableDateColumns.includes(name),
          filterEnabled: filterCol?.filteringEnabled ?? true,
          sortingEnabled: this.columnsInfo[name]?.sortingEnabled ?? true,
        };

        // JSON fields need accessorFn to stringify the value
        if (columnType === "Json") {
          return {
            ...baseColumn,
            id: name,
            accessorFn: (row: any) => {
              const val = row[name];
              if (val == null) return "";
              const s = JSON.stringify(val);
              return s.length > 100 ? s.substring(0, 100) + "..." : s;
            },
          };
        }

        return {
          ...baseColumn,
          accessorKey: name,
        };
      });
  }

  // Lista de columnas para el ColumnMenu (usado en ListToolbox)
  get columnMenuItems(): { id: string; header: string }[] {
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => ({
        id: name,
        header: this.columnTitles[name] || name,
      }));
  }

  editionDocumentationFormat(e: EditionDocumentation) {
    return {
      finalDocumentPdf: e.finalDocumentPdf,
      documentName: e.documentName,
      works: e.works
        ? e.works.map((x: any) => musicalWorkDisplayId(x, this.langStore))
        : [],
      territories: e.territories
        ? e.territories.map((x: any) => territoryDisplayId(x, this.langStore))
        : [],
      authors: e.authors
        ? e.authors.map((x: any) => authorComposerDisplayId(x, this.langStore))
        : [],
      authorsDisplay: e.authorsDisplay,
      publishers: e.publishers
        ? e.publishers.map((x: any) =>
            editionContractPublisherDisplayId(x, this.langStore)
          )
        : [],
      publisherConfiguration: e.publisherConfiguration,
      publisherConfigurationDisplay: e.publisherConfigurationDisplay,
      territoriesDisplay: e.territoriesDisplay,
      contractDuration: e.contractDuration,
      contractRenewal: e.contractRenewal,
      contractNumber: e.contractNumber,
      id: e.id,
    };
  }

  get gridState(): any {
    // Solo guardar filtros si están habilitados Y tienen valores
    const hasActiveFilters =
      this.showColumnFilters && this.filtering.length > 0;
    return {
      columnOrder: this.columnOrder,
      columnsInfo: this.columnsInfo,
      sorting: this.sorting,
      filtering: hasActiveFilters ? this.filtering : [],
      // showColumnFilters solo true si hay filtros activos
      showColumnFilters: hasActiveFilters,
      viewMode: this.viewMode,
      VERSION,
    };
  }

  setGridState(gridState: EditionDocumentationListProfile) {
    // Nunca debe fallar la carga del profile - el cliente manda, el profile sugiere
    try {
      if (!gridState || typeof gridState !== "object") return;
      // No rechazar por VERSION diferente - validamos cada campo individualmente
      // Esto permite migrar profiles de versiones anteriores

      const validColumns = new Set(Object.keys(this.columnsInfo));

      if (gridState.columnsInfo && typeof gridState.columnsInfo === "object") {
        const mergedColumnsInfo: ColumnInfo = {};

        for (const [key, defaultValue] of Object.entries(this.columnsInfo)) {
          mergedColumnsInfo[key] = { ...defaultValue };
        }

        for (const [key, savedValue] of Object.entries(gridState.columnsInfo)) {
          if (
            validColumns.has(key) &&
            typeof savedValue === "object" &&
            savedValue !== null
          ) {
            const defaultValue = mergedColumnsInfo[key];

            mergedColumnsInfo[key] = {
              sortingEnabled:
                typeof savedValue.sortingEnabled === "boolean"
                  ? savedValue.sortingEnabled
                  : defaultValue.sortingEnabled,

              width:
                typeof savedValue.width === "number" &&
                savedValue.width > 0 &&
                isFinite(savedValue.width)
                  ? savedValue.width
                  : defaultValue.width,

              isHidden:
                typeof savedValue.isHidden === "boolean"
                  ? savedValue.isHidden
                  : defaultValue.isHidden,
            };
          }
        }

        this.columnsInfo = mergedColumnsInfo;
      }

      if (Array.isArray(gridState.columnOrder)) {
        const validOrderColumns = gridState.columnOrder.filter(
          (col) => typeof col === "string" && validColumns.has(col)
        );
        const newColumns = Array.from(validColumns).filter(
          (col) => !gridState.columnOrder.includes(col)
        );
        this.columnOrder = [...validOrderColumns, ...newColumns];
      }

      if (Array.isArray(gridState.sorting)) {
        this.sorting = gridState.sorting.filter(
          (sort) =>
            typeof sort === "object" &&
            sort !== null &&
            typeof sort.columnName === "string" &&
            validColumns.has(sort.columnName) &&
            (sort.direction === "asc" || sort.direction === "desc")
        );
        if (this.sorting.length === 0) {
          this.sorting = [
            { columnName: "contractNumber", direction: SortOrder.Asc },
          ];
        }
      }

      if (Array.isArray(gridState.filtering)) {
        const nullableDateColumns = this.nullableDateColumns;
        this.filtering = gridState.filtering.filter(
          (filter) =>
            typeof filter === "object" &&
            filter !== null &&
            typeof filter.columnName === "string" &&
            validColumns.has(filter.columnName) &&
            typeof filter.operation === "string" &&
            filter.hasOwnProperty("value") &&
            // "Vacío" y "No vacío" solo en una fecha que puede ser nula. Un perfil
            // guardado cuando la columna era otra cosa no puede dejar un filtro que
            // el server rechaza en cada carga (el DataGrid también lo descarta, pero
            // el perfil puede llegar acá antes).
            isOperatorAvailable(
              this.getMrtColumnType(this.columnTypes[filter.columnName]),
              filter.operation as FilterOperator,
              nullableDateColumns.includes(filter.columnName)
            )
        );
      }

      // Si hay filtros guardados, mostrar los filtros automáticamente
      // Si el profile guardó showColumnFilters, usar ese valor
      if (typeof gridState.showColumnFilters === "boolean") {
        this.showColumnFilters = gridState.showColumnFilters;
      } else if (this.filtering.length > 0) {
        // Compatibilidad: si hay filtros pero no se guardó showColumnFilters, activarlos
        this.showColumnFilters = true;
      }

      if (gridState.viewMode === "route" || gridState.viewMode === "drawer") {
        this.viewMode = gridState.viewMode;
      }
    } catch (error) {
      // Silenciar errores de carga de profile - usar valores por defecto
      console.warn(
        "[GridDataStore] Error loading profile, using defaults:",
        error
      );
    }
  }
}
